/* eslint-disable camelcase */

import {
  AccountDetailsEditActionsEnum,
  AccountDetailsEditAttributesEnum,
} from '../../../../../../../src/components/accounts/tracking/accountDetailsTrackingHelpers';
import {
  buildEditTrackingPayload,
  ContactDetailsEventTypesEnum,
  ContactDetailsTrackingEvent,
} from '../../../../../../../src/components/contacts/contactDetailsTrackingHelpers';
import {CardGroupModel} from '../../../../../../../src/models/api/cardGroupModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../../src/services/tracking';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';

/*
 * Types.
 */

export interface ContactsManagerAccountViewPayload {
  account_id: number;
  account_location?: string;
}

export interface ContactsManagerAccountCreatePayload {
  has_domains: boolean;
}

export interface ContactsManagerAccountImportPayload {
  method: 'csv';
  num_accounts: number;
}

export interface ContactsManagerAccountImportErredPayload {
  error_type: string;
}

export interface ContactsManagerAccountEditPayload {
  account_id: number;
  action: AccountDetailsEditActionsEnum;
  attribute: AccountDetailsEditAttributesEnum;
}

export interface ContactsManagerAccountDeletePayload {
  account_id: number;
}

export interface ContactsManagerAccountCopyLinkPayload {
  account_id: number;
}

/*
 * Helpers.
 */

export function buildContactsManagerAccountViewPayload(
  accountId: number,
  accountLocation?: ContactsManagerParametersTypesEnum,
): ContactsManagerAccountViewPayload {
  return {
    account_id: accountId,
    account_location: accountLocation?.toLowerCase(),
  };
}

export function buildContactsManagerAccountCreatePayload(
  hasDomains: boolean,
): ContactsManagerAccountCreatePayload {
  return {
    has_domains: hasDomains,
  };
}

export function buildContactsManagerAccountImportPayload(
  numberOfAccountsImported: number,
): ContactsManagerAccountImportPayload {
  return {
    method: 'csv',
    num_accounts: numberOfAccountsImported,
  };
}

export function buildContactsManagerAccountImportErredPayload(
  errorType: string,
): ContactsManagerAccountImportErredPayload {
  return {
    error_type: errorType,
  };
}

export function buildContactsManagerAccountEditPayload(
  accountId: number,
  action: AccountDetailsEditActionsEnum,
  attribute: AccountDetailsEditAttributesEnum,
): ContactsManagerAccountEditPayload {
  return {
    account_id: accountId,
    action,
    attribute,
  };
}

export function buildContactsManagerAccountDeletePayload(
  accountId: number,
): ContactsManagerAccountDeletePayload {
  return {
    account_id: accountId,
  };
}

export function buildContactsManagerAccountCopyLinkPayload(
  accountId: number,
): ContactsManagerAccountCopyLinkPayload {
  return {
    account_id: accountId,
  };
}

/*
 * Constants.
 */

export enum ContactsManagerEventTypesEnum {
  CREATE_CONTACT = 'create_contact',
  DELETE_CONTACT = 'delete_contact',
  CREATE_CARD_GROUP = 'create_card_group',
  EDIT_CARD_GROUPS = 'edit_card_groups',
  MESSAGE_CONTACT = 'message_contact',
  INFO_BOX_CLICK = 'info_box_click',
}

export enum ContactsManagerEditCardGroupsTrackingActionsEnum {
  ADD = 'add',
  REMOVE = 'remove',
}

export enum ContactsManagerInfoBoxActionsEnum {
  DISMISS = 'dismiss',
  EXPAND = 'expand',
  LEARN_MORE = 'learn_more',
}

export enum ContactsManagerUpdateCardGroupPropertiesEnum {
  NAME = 'name',
}

/*
 * Event Types.
 */

interface ContactsManagerEvent {
  type: ContactsManagerEventTypesEnum;
}

export interface ContactsManagerCreateContactEvent extends ContactsManagerEvent {
  type: ContactsManagerEventTypesEnum.CREATE_CONTACT;
  namespace: string;
  isAccountAdded: boolean;
  isCardGroupsAssigned: boolean;
}

export interface ContactsManagerDeleteContactEvent extends ContactsManagerEvent {
  type: ContactsManagerEventTypesEnum.DELETE_CONTACT;
  numDeleted: number;
  namespaces: ReadonlyArray<string>;
}

export interface ContactsManagerCreateCardGroupEvent extends ContactsManagerEvent {
  type: ContactsManagerEventTypesEnum.CREATE_CARD_GROUP;
  cardGroup: CardGroupModel;
}

export interface ContactsManagerEditCardGroupsEvent extends ContactsManagerEvent {
  type: ContactsManagerEventTypesEnum.EDIT_CARD_GROUPS;
  numContactsEdited?: number;
  action: ContactsManagerEditCardGroupsTrackingActionsEnum;
}

export interface ContactsManagerMessageContactEvent extends ContactsManagerEvent {
  type: ContactsManagerEventTypesEnum.MESSAGE_CONTACT;
  numMessaged?: number;
}

export interface ContactsManagerInfoBoxClickEvent extends ContactsManagerEvent {
  type: ContactsManagerEventTypesEnum.INFO_BOX_CLICK;
  action?: ContactsManagerInfoBoxActionsEnum;
}

/*
 * Union of Events and Handler.
 */

export type ContactsManagerTrackingEvent =
  | ContactsManagerCreateContactEvent
  | ContactsManagerDeleteContactEvent
  | ContactsManagerCreateCardGroupEvent
  | ContactsManagerMessageContactEvent
  | ContactsManagerInfoBoxClickEvent
  | ContactsManagerEditCardGroupsEvent;
export type ContactsManagerTrackEventHandler = (event: ContactsManagerTrackingEvent) => void;

/*
 * Event Creators.
 */

export function buildContactsManagerCreateContactEvent(
  namespace: string,
  isAccountAdded: boolean,
  isCardGroupsAssigned: boolean,
): ContactsManagerCreateContactEvent {
  return {
    type: ContactsManagerEventTypesEnum.CREATE_CONTACT,
    namespace,
    isAccountAdded,
    isCardGroupsAssigned,
  };
}

export function buildContactsManagerDeleteContactEvent(
  numDeleted: number,
  namespaces: ReadonlyArray<string>,
): ContactsManagerDeleteContactEvent {
  return {
    type: ContactsManagerEventTypesEnum.DELETE_CONTACT,
    numDeleted,
    namespaces,
  };
}

export function buildContactsManagerCreateCardGroupEvent(
  cardGroup: CardGroupModel,
): ContactsManagerCreateCardGroupEvent {
  return {
    type: ContactsManagerEventTypesEnum.CREATE_CARD_GROUP,
    cardGroup,
  };
}

export function buildContactsManagerEditCardGroupsEvent(
  action: ContactsManagerEditCardGroupsTrackingActionsEnum,
  numContactsEdited: number,
): ContactsManagerEditCardGroupsEvent {
  return {
    type: ContactsManagerEventTypesEnum.EDIT_CARD_GROUPS,
    action,
    numContactsEdited,
  };
}

export function buildContactsManagerMessageEvent(numMessaged: number): ContactsManagerMessageContactEvent {
  return {
    type: ContactsManagerEventTypesEnum.MESSAGE_CONTACT,
    numMessaged,
  };
}

export function buildContactsManagerInfoBoxClickEvent(
  action: ContactsManagerInfoBoxActionsEnum,
): ContactsManagerInfoBoxClickEvent {
  return {
    type: ContactsManagerEventTypesEnum.INFO_BOX_CLICK,
    action,
  };
}

export function ContactsManagerContactDetailsTrackingHandler(
  parameterType: ContactsManagerParametersTypesEnum,
  event: ContactDetailsTrackingEvent,
) {
  const sectionType = parameterType.toLowerCase();
  switch (event.type) {
    case ContactDetailsEventTypesEnum.VIEW: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_DETAILS_VIEW, {
        contact_card_id: event.cardId,
        is_popout: event.isPopout,
        section_type: sectionType,
      });
      break;
    }
    case ContactDetailsEventTypesEnum.EDIT: {
      const payload = buildEditTrackingPayload(event.cardAttributeUpdateAction, event.card);
      if (payload) {
        trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_EDIT, {
          contact_card_id: payload.contact_card_id,
          namespace: payload.namespace,
          action: payload.action,
          attribute: payload.attribute,
          section_type: sectionType,
        });
      }
      break;
    }
    case ContactDetailsEventTypesEnum.ADD_TO_CONTACTS: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_EDIT, {
        contact_card_id: event.cardId,
        namespace: event.namespace,
        action: 'add',
        attribute: 'contact_type',
        section_type: parameterType,
      });
      break;
    }
    case ContactDetailsEventTypesEnum.CONVERT_CONTACT_TYPE: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_EDIT, {
        contact_card_id: event.card.id,
        namespace: event.card.namespace,
        action: 'edit',
        attribute: 'contact_type',
        section_type: parameterType,
      });
      break;
    }
    case ContactDetailsEventTypesEnum.EDIT_ACCOUNT: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_EDIT, {
        contact_card_id: event.cardId,
        namespace: event.namespace,
        action: event.action,
        attribute: 'account',
        section_type: sectionType,
      });
      break;
    }
    case ContactDetailsEventTypesEnum.CREATE_CARD_GROUP: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CARD_GROUP_CREATE, {
        section_type: sectionType,
        namespace: event.cardGroup.namespace,
      });
      break;
    }
    case ContactDetailsEventTypesEnum.EDIT_CARD_GROUP: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_CARD_GROUP_EDIT, {
        section_type: sectionType,
        action:
          event.action === 'delete' ? ContactsManagerEditCardGroupsTrackingActionsEnum.REMOVE : event.action,
        num_edited: 1,
      });
      break;
    }
    default:
    // Do not track anything
  }
}

export function ContactsManagerTrackingHandler(
  parameterType: ContactsManagerParametersTypesEnum,
  event: ContactsManagerTrackingEvent,
) {
  const sectionType = parameterType.toLowerCase();
  switch (event.type) {
    case ContactsManagerEventTypesEnum.CREATE_CONTACT: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_CREATE, {
        section_type: sectionType,
        namespace: event.namespace,
        if_account_added: event.isAccountAdded,
        if_assigned_list: event.isCardGroupsAssigned,
      });
      break;
    }
    case ContactsManagerEventTypesEnum.DELETE_CONTACT: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_DELETE, {
        section_type: sectionType,
        namespaces: event.namespaces,
        num_deleted: event.numDeleted,
      });
      break;
    }
    case ContactsManagerEventTypesEnum.CREATE_CARD_GROUP: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CARD_GROUP_CREATE, {
        section_type: sectionType,
        namespace: event.cardGroup.namespace,
      });
      break;
    }
    case ContactsManagerEventTypesEnum.EDIT_CARD_GROUPS: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_CARD_GROUP_EDIT, {
        section_type: sectionType,
        action: event.action,
        num_edited: event?.numContactsEdited,
      });
      break;
    }
    case ContactsManagerEventTypesEnum.MESSAGE_CONTACT: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_MESSAGE, {
        section_type: sectionType,
        num_messaged: event.numMessaged,
      });
      break;
    }
    case ContactsManagerEventTypesEnum.INFO_BOX_CLICK: {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_INFO_BOX_CLICK, {
        section_type: sectionType,
        action: event.action,
      });
      break;
    }
    default:
    // Do not track anything
  }
}
